import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { useForm } from 'react-hook-form';
import { Button } from 'primereact/button';
import { toast } from 'layout';
import { TIMEZONE_VN, TOAST } from 'constant';
import { getEndingTimeOfDate, getMaxDate, mapError } from 'utils/func';
import { DatePicker } from 'components/FormControl';

const ChangeDeadline = forwardRef(({ mutate, isLoading, refetchProjectProposals }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [deadlineControl, setDeadlineControl] = useState(null);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, dirtyFields },
  } = useForm({ mode: 'onChange' });

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_deadlineControl) => {
        setDeadlineControl(_deadlineControl);
        setVisible(true);

        const deadline = getMaxDate(
          _deadlineControl?.value?.organizationPrequalificationDeadline,
          _deadlineControl?.value?.program?.organizationPrequalificationDeadline
        );

        reset({
          organizationPrequalificationDeadline: deadline?.getTime()
            ? getEndingTimeOfDate(deadline)
            : null,
        });
      },
    }),
    []
  );

  const onSubmit = (data) => {
    if (
      getEndingTimeOfDate(new Date(data?.organizationPrequalificationDeadline)) <
      new Date(deadlineControl?.value?.program?.organizationPrequalificationDeadline)
    ) {
      toast(
        TOAST.WARN,
        `${t('errorMessage.notAllowSmallerThanOrganizationPrequalificationDeadline')}: ${new Date(
          deadlineControl?.value?.program?.organizationPrequalificationDeadline
        ).toLocaleDateString('en-GB', TIMEZONE_VN)}`
      );
      return;
    }

    mutate(
      {
        id: deadlineControl?.value?.id,
        organizationPrequalificationDeadline: data?.organizationPrequalificationDeadline
          ? getEndingTimeOfDate(new Date(data?.organizationPrequalificationDeadline))
          : null,
      },
      {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('projectProposal.changeDeadline.success'));
          reset();
          refetchProjectProposals();
          setVisible(false);
        },
        onError: (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      }
    );
  };

  // #endregion Event

  return (
    <Dialog
      header={
        <div className="flex justify-content-between text-orange-400">
          {t('projectProposal.changeDeadline.title')}
        </div>
      }
      draggable={false}
      visible={visible}
      style={{ width: '450px' }}
      onHide={() => {
        reset();
        setVisible(false);
      }}
      closable={!isLoading}
    >
      <div className="relative">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-fluid flex flex-column"
          autoComplete="off"
        >
          <p className="text-lg font-bold">
            {t('projectProposal.label')}:{' '}
            {t('lang') === 'vi'
              ? deadlineControl?.value?.vietnameseName
              : deadlineControl?.value?.englishName}
          </p>
          <div className="grid">
            <div className="col-12 mb-2">
              <DatePicker
                name="organizationPrequalificationDeadline"
                control={control}
                errors={errors}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-6">
              <Button
                label={t('formLayout.action.cancel')}
                type="button"
                severity="danger"
                onClick={() => {
                  reset();
                  setVisible(false);
                }}
                disabled={isLoading}
              />
            </div>
            <div className="col-6">
              <Button
                label={t('formLayout.action.save')}
                className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                type="submit"
                disabled={isLoading || !(Object.keys(dirtyFields)?.length > 0)}
              />
            </div>
          </div>
        </form>
      </div>
    </Dialog>
  );
});

ChangeDeadline.propTypes = {
  mutate: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  refetchProjectProposals: PropTypes.func.isRequired,
};

export default ChangeDeadline;
